// Custom Analytics Utility for Landing Page
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type based on screen width and user agent
const getDeviceType = () => {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for tablets first (iPad, Android tablets)
  if (/ipad|tablet|playbook|silk/i.test(userAgent) || 
      (width >= 768 && width <= 1024 && /mobile|android/i.test(userAgent))) {
    return 'tablet';
  }
  
  // Check for mobile devices
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile|wpdesktop/i.test(userAgent) || 
      width < 768) {
    return 'mobile';
  }
  
  // Default to desktop
  return 'desktop';
};

// Get browser name
const getBrowserName = () => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  
  return 'Other';
};

// Get OS name
const getOSName = () => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Win')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
  
  return 'Other';
};

// Track an analytics event
export const trackEvent = async (eventType, eventName, metadata = {}) => {
  try {
    const sessionId = getSessionId();
    
    await fetch(`${API_URL}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: eventType,
        event_name: eventName,
        page: 'landing',
        metadata: {
          ...metadata,
          session_id: sessionId,
          timestamp: new Date().toISOString(),
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
          device_type: getDeviceType(),
          browser: getBrowserName(),
          os: getOSName(),
        },
      }),
    });
  } catch (error) {
    // Silently fail - don't interrupt user experience for analytics
    console.debug('Analytics event failed:', error);
  }
};

// Convenience methods for common events
export const trackPageView = (pageName = 'landing') => {
  trackEvent('page_view', pageName, {
    url: window.location.href,
    referrer: document.referrer || 'direct',
  });
};

export const trackButtonClick = (buttonName, buttonText = '') => {
  trackEvent('button_click', buttonName, {
    button_text: buttonText,
  });
};

export const trackSectionView = (sectionName) => {
  trackEvent('section_view', sectionName, {
    scroll_depth: Math.round((window.scrollY / document.body.scrollHeight) * 100),
  });
};

export const trackFAQClick = (questionIndex, questionText) => {
  trackEvent('faq_click', `faq_${questionIndex}`, {
    question: questionText,
  });
};

export const trackPricingClick = (planName) => {
  trackEvent('pricing_click', planName, {
    plan: planName,
  });
};

// Loyalty Ladder rung interactions (hover / tap). Called once per (rung ×
// interaction) per session by the LoyaltyLadderSection dedup ref, so the
// event volume stays low while still surfacing which rung visitors
// gravitate towards (destination bias vs. mid-tier engagement).
export const trackLoyaltyLadderInteraction = (rungLabel, interactionType) => {
  trackEvent('loyalty_ladder_interaction', `${interactionType}_${rungLabel}`, {
    rung_label: rungLabel,
    interaction_type: interactionType, // 'hover' | 'tap'
  });
};

// Track scroll depth milestones
export const trackScrollDepth = (depth) => {
  trackEvent('scroll_depth', `scroll_${depth}`, {
    depth_percentage: depth,
  });
};

// Track time on page
export const trackTimeOnPage = (duration) => {
  trackEvent('time_on_page', 'session_duration', {
    duration_seconds: duration,
    duration_formatted: formatDuration(duration),
  });
};

// Format duration in human readable format
const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Intersection Observer hook for section visibility tracking
export const createSectionObserver = (onSectionVisible) => {
  const observedSections = new Set();
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          if (sectionId && !observedSections.has(sectionId)) {
            observedSections.add(sectionId);
            onSectionVisible(sectionId);
          }
        }
      });
    },
    {
      threshold: 0.3, // Trigger when 30% of section is visible
      rootMargin: '0px',
    }
  );
  
  return observer;
};

// Create scroll depth tracker
export const createScrollDepthTracker = () => {
  const trackedMilestones = new Set();
  const milestones = [25, 50, 75, 100];
  
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    
    milestones.forEach((milestone) => {
      if (scrollPercent >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);
        trackScrollDepth(milestone);
      }
    });
  };
  
  return {
    start: () => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    },
    stop: () => {
      window.removeEventListener('scroll', handleScroll);
    },
    getTrackedMilestones: () => Array.from(trackedMilestones),
  };
};

// Create time on page tracker
export const createTimeTracker = () => {
  let startTime = null;
  let totalTime = 0;
  let isActive = true;
  let intervalId = null;
  
  const start = () => {
    startTime = Date.now();
    
    // Track visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Send periodic updates every 30 seconds
    intervalId = setInterval(() => {
      if (isActive && startTime) {
        const currentDuration = Math.floor((Date.now() - startTime) / 1000) + totalTime;
        // Only track significant time (more than 5 seconds)
        if (currentDuration > 5) {
          trackTimeOnPage(currentDuration);
        }
      }
    }, 30000);
    
    // Track on page unload
    window.addEventListener('beforeunload', handleUnload);
  };
  
  const handleVisibilityChange = () => {
    if (document.hidden) {
      // Page is hidden, pause tracking
      if (startTime) {
        totalTime += Math.floor((Date.now() - startTime) / 1000);
        startTime = null;
      }
      isActive = false;
    } else {
      // Page is visible again, resume tracking
      startTime = Date.now();
      isActive = true;
    }
  };
  
  const handleUnload = () => {
    if (startTime) {
      const finalDuration = Math.floor((Date.now() - startTime) / 1000) + totalTime;
      if (finalDuration > 5) {
        // Use sendBeacon for reliable delivery on page unload
        const sessionId = getSessionId();
        navigator.sendBeacon(
          `${API_URL}/api/analytics/event`,
          JSON.stringify({
            event_type: 'time_on_page',
            event_name: 'session_end',
            page: 'landing',
            metadata: {
              session_id: sessionId,
              duration_seconds: finalDuration,
              duration_formatted: formatDuration(finalDuration),
              timestamp: new Date().toISOString(),
            },
          })
        );
      }
    }
  };
  
  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleUnload);
  };
  
  const getCurrentDuration = () => {
    if (startTime && isActive) {
      return Math.floor((Date.now() - startTime) / 1000) + totalTime;
    }
    return totalTime;
  };
  
  return {
    start,
    stop,
    getCurrentDuration,
  };
};

export default {
  trackEvent,
  trackPageView,
  trackButtonClick,
  trackSectionView,
  trackFAQClick,
  trackPricingClick,
  trackLoyaltyLadderInteraction,
  trackScrollDepth,
  trackTimeOnPage,
  createSectionObserver,
  createScrollDepthTracker,
  createTimeTracker,
};
